import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Store, Save, ShieldCheck, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { vendorApi } from '@/services/api';
import { uploadImage } from '@/services/auth';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@nepal-ecom/ui';

interface Vendor {
  id: string;
  businessName: string;
  description?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  provinceName?: string | null;
  district?: string | null;
  municipality?: string | null;
  streetAddress?: string | null;
  panNumber?: string | null;
  vatNumber?: string | null;
  verificationStatus: string;
  tier: string;
  bankDetails?: { bankName?: string; accountNumber?: string; accountName?: string };
}

const STATUS_TONE: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  approved: 'success',
  verified: 'success',
  pending: 'warning',
  rejected: 'destructive',
  suspended: 'destructive',
};

export function Settings() {
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [businessName, setBusinessName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [district, setDistrict] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await vendorApi.getMyVendor();
      const v = res.data?.data as Vendor;
      if (v) {
        setVendor(v);
        setBusinessName(v.businessName ?? '');
        setDescription(v.description ?? '');
        setEmail(v.email ?? '');
        setPhone(v.phone ?? '');
        setWebsite(v.website ?? '');
        setDistrict(v.district ?? '');
        setMunicipality(v.municipality ?? '');
        setStreetAddress(v.streetAddress ?? '');
        setLogoUrl(v.logoUrl ?? '');
        setBankName(v.bankDetails?.bankName ?? '');
        setAccountNumber(v.bankDetails?.accountNumber ?? '');
        setAccountName(v.bankDetails?.accountName ?? '');
      }
    } catch (e) {
      toast.error('Could not load store settings', { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleSave = async () => {
    if (!vendor) return;
    setSaving(true);
    try {
      await vendorApi.updateVendor(vendor.id, {
        businessName: businessName.trim(),
        description: description.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        website: website.trim() || undefined,
        district: district.trim() || undefined,
        municipality: municipality.trim() || undefined,
        streetAddress: streetAddress.trim() || undefined,
        logoUrl: logoUrl || undefined,
        bankDetails: {
          bankName: bankName.trim(),
          accountNumber: accountNumber.trim(),
          accountName: accountName.trim() || undefined,
        },
      });
      toast.success('Store settings saved');
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message
        ?? (e as Error).message;
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>;
  }

  if (!vendor) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="Store settings" />
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <p className="font-semibold text-slate-900">No vendor profile found</p>
            <p className="text-sm text-slate-500 mt-1">Your account isn&apos;t linked to a vendor record yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader
        title="Store settings"
        description="Manage your storefront, contact info, and bank details for payouts."
        badge={
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_TONE[vendor.verificationStatus] ?? 'secondary'}>{vendor.verificationStatus}</Badge>
            <Badge variant="secondary" className="capitalize">{vendor.tier}</Badge>
          </div>
        }
        actions={
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save changes</>}
          </Button>
        }
      />

      <Card>
        <CardHeader><CardTitle className="inline-flex items-center gap-2"><Store className="h-4 w-4 text-emerald-700" /> Store profile</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">Business name</label>
            <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 resize-y" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Phone</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700">Website</label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" className="mt-1" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Store logo</label>
              <div className="mt-1 flex items-center gap-3">
                <div className="h-14 w-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                  {logoUrl
                    ? <img src={logoUrl} alt="" className="w-full h-full object-cover" />
                    : <Store className="h-6 w-6 m-4 text-slate-400" />}
                </div>
                <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm font-medium cursor-pointer hover:bg-slate-50">
                  <input type="file" accept="image/*" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingLogo(true);
                      try {
                        const url = await uploadImage(file);
                        setLogoUrl(url);
                      } catch (err) {
                        toast.error('Upload failed', { description: (err as Error).message });
                      } finally {
                        setUploadingLogo(false);
                        e.target.value = '';
                      }
                    }} />
                  {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Upload logo'}
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Address</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">District</label>
            <Input value={district} onChange={(e) => setDistrict(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Municipality</label>
            <Input value={municipality} onChange={(e) => setMunicipality(e.target.value)} className="mt-1" />
          </div>
          <div className="sm:col-span-3">
            <label className="text-xs font-semibold text-slate-700">Street address</label>
            <Input value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} className="mt-1" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-700" /> Bank account for payouts</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">Bank name</label>
            <Input value={bankName} onChange={(e) => setBankName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Account number</label>
            <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Account name</label>
            <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} className="mt-1" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
